import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { typography, radius } from '@/shared/theme/tokens';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { ErrorState } from '@/shared/ui/ErrorState';
import { useLiveModeModel } from '../../hooks/useLiveModeModel';
import { KEYFRAMES } from './recommendedTheme';
import { Header } from './Header';
import { RecQueue } from './RecQueue';
import { FurnaceColored } from './FurnaceColored';
import { SessionStrip } from './SessionStrip';
import { TelemetryRail } from './TelemetryRail';
import { AlertTicker } from './AlertTicker';

/**
 * Recommended — the synthesized default Live screen. Light AND dark (all colour
 * from useThemeColors + shared status metas; no scoped palette). Same shared
 * model/IA as every variant. Composes the best of the five:
 *   Header (Consensus) · work-queue recs w/ inline actions (AND framework) ·
 *   colored furnace (today's behavior) · telemetry gauges w/ flip numerals
 *   (HUD + Board) · session readout (Terminal) · alert ticker (News).
 */
export function RecommendedScreen({
  deploymentName, svgUrl, seedAlerts, seedRecs, isLoading, isError,
  onSelectRec, onSelectAlert, onQuickAct,
}) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { isDesktop } = useBreakpoint();
  const {
    alerts, recs, clock, flashId, jitterActive,
    degraded, rankedRecs, rankedAlerts, headline, freshness, animate,
  } = useLiveModeModel(seedAlerts, seedRecs);

  const booting = isLoading && alerts.length === 0 && recs.length === 0;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, gap: 12, padding: 12,
      background: c.bgPage, color: c.textPrimary, fontFamily: typography.fontFamily.body,
    }}>
      <style>{KEYFRAMES}</style>

      <Header
        deploymentName={deploymentName}
        clock={clock}
        jitterActive={jitterActive}
        animate={animate}
        headline={headline}
        freshness={freshness}
        c={c}
        isDark={isDark}
      />

      {isError ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ErrorState message="Couldn't load live data for this deployment." />
        </div>
      ) : booting ? (
        <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: isDesktop ? '1.5fr 1fr' : '1fr', gap: 12 }}>
          <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, padding: 16 }}><SkeletonCard rows={6} height="100%" /></div>
          {isDesktop && <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: radius.xl, padding: 16 }}><SkeletonCard rows={6} height="100%" /></div>}
        </div>
      ) : (
        <>
          {/* main row: work-queue (hero) + furnace/session */}
          <div style={{
            flex: 1, minHeight: 0,
            display: isDesktop ? 'grid' : 'flex', flexDirection: isDesktop ? undefined : 'column',
            gridTemplateColumns: isDesktop ? '1.5fr 1fr' : undefined,
            gridTemplateRows: isDesktop ? 'minmax(0, 1fr)' : undefined, gap: 12,
          }}>
            <div style={{ minWidth: 0, minHeight: isDesktop ? 0 : 360, display: 'flex' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <RecQueue
                  recs={rankedRecs}
                  allRecs={recs}
                  degraded={degraded}
                  animate={animate}
                  c={c}
                  onSelect={onSelectRec}
                  onQuickAct={onQuickAct}
                />
              </div>
            </div>
            <div style={{ minWidth: 0, minHeight: isDesktop ? 0 : 320, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FurnaceColored svgUrl={svgUrl} params={rankedAlerts} animate={animate} c={c} isDark={isDark} />
              <SessionStrip alerts={alerts} recs={recs} c={c} />
            </div>
          </div>

          {/* parameter telemetry rail */}
          <TelemetryRail alerts={rankedAlerts} flashId={flashId} animate={animate} c={c} />

          {/* live alerts ticker */}
          <AlertTicker alerts={rankedAlerts} flashId={flashId} animate={animate} c={c} onSelect={onSelectAlert} />
        </>
      )}
    </div>
  );
}
